import usePageTitle from "@/hooks/use-page-title";
import { RotateCcw, Ban, CheckCircle2, Clock, IndianRupee, AlertTriangle } from "lucide-react";

export default function ReturnsRefund() {
  usePageTitle("Returns & Refund");
  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-8 py-14">
      <div className="overline text-[#FF6B00] mb-2">Policy</div>
      <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Returns & Refund</h1>
      <p className="mt-3 text-zinc-600">Our policy on custom-printed items — because print isn't quite like buying a book off the shelf.</p>

      <div className="mt-10 p-6 rounded-2xl border-2 border-amber-100 bg-amber-50/60 flex gap-3 items-start">
        <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
        <div className="text-sm text-zinc-800">
          <b>Custom-printed products are non-refundable by default</b> unless there's a manufacturing defect or an error on our part. This is an industry-standard practice — each print is made specifically for you.
        </div>
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-4">
        <Card icon={CheckCircle2} title="What IS eligible for return/replacement">
          <ul className="list-disc pl-4 space-y-1">
            <li>Manufacturing defects (printing errors, color mismatch beyond acceptable tolerance)</li>
            <li>Damage during shipping (packaging photos required within 24 hrs of delivery)</li>
            <li>Wrong product / wrong quantity dispatched from our end</li>
            <li>Approved design not matching what was actually printed</li>
          </ul>
        </Card>
        <Card icon={Ban} title="What is NOT eligible">
          <ul className="list-disc pl-4 space-y-1">
            <li>Design mistakes in the file provided by the customer</li>
            <li>Typos / wrong information in customer-approved proofs</li>
            <li>Colour variance within accepted printing tolerance (±10%)</li>
            <li>Change of mind after production has started</li>
            <li>Custom sized / bespoke items produced to spec</li>
          </ul>
        </Card>
        <Card icon={Clock} title="Return Window">
          <ul className="list-disc pl-4 space-y-1">
            <li>Report defects/damage within <b>48 hours</b> of delivery</li>
            <li>Provide clear photographs (multiple angles) of the issue</li>
            <li>Retain original packaging until issue is resolved</li>
          </ul>
        </Card>
        <Card icon={RotateCcw} title="Resolution Options">
          <p>Where a return is approved, we typically offer:</p>
          <ol className="list-decimal pl-4 space-y-1 mt-1">
            <li><b>Free replacement</b> — we reprint and reship at no additional cost (preferred)</li>
            <li><b>Store credit</b> — for future orders</li>
            <li><b>Refund</b> to the original payment method (subject to review)</li>
          </ol>
        </Card>
        <Card icon={IndianRupee} title="Refund Timeline">
          <ul className="list-disc pl-4 space-y-1">
            <li>Once approved, refunds are processed within <b>5–7 working days</b></li>
            <li>Amount reflected in original payment source within 7–14 working days depending on your bank/UPI provider</li>
            <li>Cash-on-Delivery orders refunded via UPI / bank transfer</li>
          </ul>
        </Card>
        <Card icon={AlertTriangle} title="Cancellation">
          <ul className="list-disc pl-4 space-y-1">
            <li>Orders can be cancelled anytime <b>before production begins</b> — full refund</li>
            <li>Cancellation after production started: material cost may be deducted</li>
            <li>Cancellation after dispatch: not possible</li>
          </ul>
        </Card>
      </div>

      <div className="mt-10 p-6 rounded-2xl border-2 border-orange-100 bg-orange-50/70">
        <div className="font-bold text-lg mb-1">Raise a return request</div>
        <p className="text-sm text-zinc-700">Email <a href="mailto:print@brothersdigital.in" className="text-[#FF6B00] font-bold hover:underline">print@brothersdigital.in</a>, call our Customer Care on <a href="tel:02247427059" className="text-[#FF6B00] font-bold hover:underline">022 4742 7059</a> or WhatsApp <a href="https://wa.me/918070970980" className="text-[#25D366] font-bold hover:underline">+91 80709 70980</a> with your order number, issue description and clear photos. We respond within 24 working hours.</p>
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="p-6 rounded-2xl border-2 border-zinc-100 bg-white hover:border-[#FF6B00] transition-colors">
      <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center mb-3">
        <Icon size={18} className="text-[#FF6B00]" />
      </div>
      <div className="font-bold text-lg mb-2">{title}</div>
      <div className="text-sm text-zinc-600 leading-relaxed">{children}</div>
    </div>
  );
}
